import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { API } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Package, Truck, Target, CheckCircle2, AlertTriangle, Search } from "lucide-react";
import { toast } from "sonner";

const fmtNum = (v) => (Number(v) || 0).toLocaleString("pt-BR");

const StatCard = ({ icon: Icon, label, value, color, valueClass = "", testId }) => (
  <Card className="border border-border shadow-none">
    <CardContent className="p-4 flex items-center gap-3">
      <div className={`h-10 w-10 rounded-lg flex items-center justify-center shrink-0 ${color}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div className="min-w-0">
        <p className="text-xs text-muted-foreground uppercase tracking-wider">{label}</p>
        <p className={`font-heading text-xl lg:text-2xl font-black tracking-tighter truncate ${valueClass}`} data-testid={testId}>{value}</p>
      </div>
    </CardContent>
  </Card>
);

export const DeliveryControlReport = () => {
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState("all");
  const [term, setTerm] = useState("");
  const [items, setItems] = useState([]);
  const [totals, setTotals] = useState({ contract_total: 0, delivered_qty: 0, remaining_qty: 0 });
  const [loading, setLoading] = useState(false);

  const fetchClients = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/clients`, { withCredentials: true });
      setClients(data);
    } catch { /* silent */ }
  }, []);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const params = clientId && clientId !== "all" ? { client_id: clientId } : {};
      const { data } = await axios.get(`${API}/reports/delivery-control`, { params, withCredentials: true });
      setItems(data.items || []);
      setTotals(data.totals || { contract_total: 0, delivered_qty: 0, remaining_qty: 0 });
    } catch (err) {
      toast.error("Erro ao carregar controle de entregas");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => { fetchClients(); }, [fetchClients]);
  useEffect(() => { fetchReport(); }, [fetchReport]);

  const filtered = useMemo(() => {
    const t = term.trim().toLowerCase();
    if (!t) return items;
    return items.filter((it) =>
      (it.product_name || "").toLowerCase().includes(t) ||
      (it.product_code || "").toLowerCase().includes(t) ||
      (it.client_name || "").toLowerCase().includes(t)
    );
  }, [items, term]);

  const withContract = filtered.filter((i) => i.contract_total > 0);
  const withoutContract = filtered.filter((i) => i.contract_total <= 0);
  const completedContracts = withContract.filter((i) => i.remaining_qty <= 0).length;
  const criticalContracts = withContract.filter((i) => i.percent_delivered >= 80 && i.remaining_qty > 0).length;

  const percentTotals = totals.contract_total > 0
    ? Math.min((totals.delivered_qty / totals.contract_total) * 100, 100)
    : 0;

  return (
    <div className="space-y-4" data-testid="delivery-control-report">
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar produto ou cliente..."
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            className="pl-9"
            data-testid="delivery-search-input"
          />
        </div>
        <Select value={clientId} onValueChange={setClientId}>
          <SelectTrigger className="w-64" data-testid="delivery-client-filter">
            <SelectValue placeholder="Todos os clientes" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os clientes</SelectItem>
            {clients.map((c) => (
              <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard icon={Target} label="Contrato Total" value={fmtNum(totals.contract_total)} color="bg-blue-100 text-blue-700" testId="stat-contract-total" />
        <StatCard icon={Truck} label="Entregue" value={fmtNum(totals.delivered_qty)} color="bg-green-100 text-green-700" testId="stat-delivered" />
        <StatCard icon={Package} label="Saldo a Entregar" value={fmtNum(totals.remaining_qty)} color="bg-amber-100 text-amber-700" testId="stat-remaining" />
        <StatCard
          icon={percentTotals >= 100 ? CheckCircle2 : AlertTriangle}
          label="Cumprimento"
          value={`${percentTotals.toFixed(1)}%`}
          color={percentTotals >= 100 ? "bg-emerald-100 text-emerald-700" : percentTotals >= 80 ? "bg-red-100 text-red-700" : "bg-slate-100 text-slate-700"}
          testId="stat-percent"
        />
      </div>

      {/* Summary badges */}
      {(completedContracts > 0 || criticalContracts > 0) && (
        <div className="flex flex-wrap gap-2 text-xs">
          {completedContracts > 0 && (
            <Badge className="bg-green-100 text-green-800 border-green-300">
              <CheckCircle2 className="h-3 w-3 mr-1" />{completedContracts} contrato(s) concluido(s)
            </Badge>
          )}
          {criticalContracts > 0 && (
            <Badge className="bg-red-100 text-red-800 border-red-300">
              <AlertTriangle className="h-3 w-3 mr-1" />{criticalContracts} contrato(s) acima de 80%
            </Badge>
          )}
        </div>
      )}

      {/* Products with contract */}
      <Card className="border border-border shadow-none">
        <CardContent className="p-0 overflow-x-auto">
          <Table className="min-w-[900px]">
            <TableHeader>
              <TableRow>
                <TableHead className="w-[110px]">Codigo</TableHead>
                <TableHead>Produto</TableHead>
                <TableHead className="w-[180px]">Cliente</TableHead>
                <TableHead className="w-[100px] text-right">Contrato</TableHead>
                <TableHead className="w-[100px] text-right">Entregue</TableHead>
                <TableHead className="w-[100px] text-right">Saldo</TableHead>
                <TableHead className="w-[220px]">Progresso</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">Carregando...</TableCell></TableRow>
              ) : withContract.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                  Nenhum produto com contrato cadastrado. Defina o &quot;Total de Kits a Produzir&quot; na aba Produtos.
                </TableCell></TableRow>
              ) : (
                withContract.map((it) => {
                  const percent = it.percent_delivered || 0;
                  const isDone = it.remaining_qty <= 0;
                  const isCritical = !isDone && percent >= 80;
                  return (
                    <TableRow key={it.product_id} data-testid={`delivery-row-${it.product_id}`}>
                      <TableCell className="font-mono text-xs">{it.product_code || "-"}</TableCell>
                      <TableCell className="font-medium max-w-[280px]">
                        <span className="block truncate" title={it.product_name}>{it.product_name}</span>
                        {it.drawing_number && <span className="text-[10px] text-muted-foreground">Desenho: {it.drawing_number}</span>}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground truncate max-w-[180px]">{it.client_name || "-"}</TableCell>
                      <TableCell className="text-right font-mono text-sm">{fmtNum(it.contract_total)}</TableCell>
                      <TableCell className="text-right font-mono text-sm">{fmtNum(it.delivered_qty)}</TableCell>
                      <TableCell className={`text-right font-mono text-sm font-semibold ${isDone ? "text-green-700" : isCritical ? "text-red-700" : ""}`}>
                        {fmtNum(it.remaining_qty)}
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          <div className="h-2 w-full bg-muted rounded-full overflow-hidden">
                            <div
                              className={`h-full transition-all ${isDone ? "bg-green-600" : isCritical ? "bg-red-500" : "bg-primary"}`}
                              style={{ width: `${Math.min(percent, 100)}%` }}
                            />
                          </div>
                          <div className="flex items-center justify-between text-[10px] font-medium">
                            <span className={isDone ? "text-green-700" : isCritical ? "text-red-700" : "text-muted-foreground"}>
                              {percent.toFixed(1)}%
                            </span>
                            {isDone && <span className="text-green-700 font-semibold">Concluido</span>}
                            {isCritical && <span className="text-red-700 font-semibold">Atencao</span>}
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Products without contract - collapsed hint */}
      {withoutContract.length > 0 && (
        <div className="text-xs text-muted-foreground bg-muted/30 border border-dashed rounded-md p-3">
          <strong>{withoutContract.length}</strong> produto(s) sem contrato definido nesta selecao. Cadastre o &quot;Total de Kits a Produzir&quot; na aba Produtos para incluir no controle.
        </div>
      )}
    </div>
  );
};
